import React from 'react';
import { Box, Paper, Typography, Divider, Tooltip, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../../helpers/formatCentsToUsd';
import InfoIcon from '@mui/icons-material/Info';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EventSeatIcon from '@mui/icons-material/EventSeat';

function OneTicket({ item, hasSeatMismatch = false, onClick, showSeatName = true }) {
    const { t } = useTranslation();

    return (
        <Paper
            elevation={2}
            sx={{
                mb: 1,
                display: 'flex',
                // Stack vertically on phones so the seat, ticket info and price
                // each get the full width instead of cramming into one row.
                flexDirection: { xs: 'column', sm: 'row' },
                overflow: 'hidden',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: 4,
                    borderColor: 'primary.main',
                },
                opacity: item?.shouldDelete ? 0.4 : 1,
            }}
            onClick={onClick || (() => {})}
        >
            {/* Left section - Seat */}
            {item.ticketSeatName && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: hasSeatMismatch ? 'error.main' : 'primary.main',
                        color: hasSeatMismatch ? 'error.contrastText' : 'primary.contrastText',
                        px: 2,
                        py: { xs: 0.5, sm: 0 },
                        flexShrink: 0,
                    }}
                >
                    {hasSeatMismatch && (
                        <Tooltip title={t('bookingitems.validation.seatNotCompatible')}>
                            <ErrorOutlineIcon sx={{ fontSize: 14, mb: -0.5 }} />
                        </Tooltip>
                    )}
                    {showSeatName ? (
                        <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
                            {item.ticketSeatName}
                        </Typography>
                    ) : (
                        // Agents without seat-management rights see only that a seat
                        // is assigned, never the actual seat number.
                        <EventSeatIcon sx={{ fontSize: 18 }} />
                    )}
                </Box>
            )}

            {/* Divider between seat and info (only meaningful in the row layout) */}
            <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', sm: 'block' } }}
            />

            {/* Middle section - Ticket info */}
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <ConfirmationNumberIcon
                        sx={{ fontSize: 18, mr: 1, color: 'primary.main', flexShrink: 0 }}
                    />
                    <Typography variant="body1" fontWeight="medium">
                        {item.ticketOptionName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {item.ticketTypeName}
                    </Typography>
                </Box>

                {/* Notes */}
                {item.note && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            mt: 0.5,
                            display: 'block',
                        }}
                    >
                        <strong>{t('bookingitems.columns.note')}:</strong> {item.note}
                    </Typography>
                )}
                {item.checkinNote && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            mt: 0.5,
                            display: 'block',
                        }}
                    >
                        <strong>{t('bookingitems.columns.checkinNote')}:</strong> {item.checkinNote}
                    </Typography>
                )}
            </Box>

            {/* Right section - Price */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    px: 1,
                    py: 1,
                    // Separator sits on the left in the row layout, on top when stacked.
                    borderLeft: { xs: 'none', sm: '2px dashed' },
                    borderTop: { xs: '2px dashed', sm: 'none' },
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary.main"
                    sx={{ textAlign: 'center' }}
                >
                    {formatCentsToUsd(item.finalPrice)}
                </Typography>
                <Tooltip
                    title={t('bookingitems.netPriceDescription', {
                        price: formatCentsToUsd(item.price),
                    })}
                >
                    <IconButton>
                        <InfoIcon fontSize="small" color="action" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
}

export default OneTicket;
